import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { addSuppressions } from '../services/campaigns';
import { isDeliverableStatus, parseSuppressionInput } from '../lib/suppressionImport';

const BLANK_STATUS_LABEL = '(blank)';

/** Paste a list or upload a CSV (e.g. a verification service's results) to suppress many addresses at once. */
export default function BulkSuppressionImport() {
  const inputRef = useRef<HTMLInputElement>(null);
  const [source, setSource] = useState('');
  const [fileName, setFileName] = useState('');
  const [note, setNote] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const parsed = useMemo(() => (source.trim() ? parseSuppressionInput(source) : null), [source]);

  const statusCounts = useMemo(() => {
    const counts = new Map<string, number>();
    parsed?.candidates.forEach(({ status }) => counts.set(status, (counts.get(status) ?? 0) + 1));
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [parsed]);

  // New input resets the choice to "everything that isn't a good result".
  useEffect(() => {
    setSelectedStatuses(
      new Set(statusCounts.map(([status]) => status).filter((status) => !isDeliverableStatus(status))),
    );
  }, [statusCounts]);

  const toAdd = useMemo(() => {
    if (!parsed) return [];
    if (!parsed.statusColumn) return parsed.candidates;
    return parsed.candidates.filter(({ status }) => selectedStatuses.has(status));
  }, [parsed, selectedStatuses]);

  async function handleFile(file: File) {
    setError(null);
    setResult(null);
    try {
      setSource(await file.text());
      setFileName(file.name);
      setNote(`Imported from ${file.name}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not read that file');
    }
  }

  function clearInput() {
    setSource('');
    setFileName('');
    setNote('');
  }

  function toggleStatus(status: string, checked: boolean) {
    setSelectedStatuses((current) => {
      const next = new Set(current);
      if (checked) next.add(status);
      else next.delete(status);
      return next;
    });
  }

  async function handleSubmit() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const { added, alreadySuppressed } = await addSuppressions(toAdd, note);
      setResult(
        `Added ${added} address${added === 1 ? '' : 'es'} to the suppression list.` +
          (alreadySuppressed > 0 ? ` ${alreadySuppressed} were already on it and were left unchanged.` : ''),
      );
      clearInput();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add those addresses');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1" component="h2">
            Add many addresses
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Paste addresses (one per line, or separated by commas), or upload a CSV with an{' '}
            <strong>Email</strong> column. A results file from ZeroBounce, NeverBounce, or a Postmark
            bounce export works as-is — you choose which results to block. Campaigns already in
            progress skip these addresses from the next send onward.
          </Typography>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        {result && (
          <Alert severity="info" role="status" onClose={() => setResult(null)}>
            {result}
          </Alert>
        )}

        {fileName ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={fileName} onDelete={clearInput} />
          </Stack>
        ) : (
          <TextField
            label="Paste addresses"
            multiline
            minRows={4}
            maxRows={12}
            value={source}
            onChange={(event) => {
              setSource(event.target.value);
              setResult(null);
            }}
            inputProps={{ spellCheck: false, autoCapitalize: 'none' }}
          />
        )}

        <Box>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void handleFile(file);
              // Reset so re-picking the same file still fires a change event.
              event.target.value = '';
            }}
          />
          <Button variant="outlined" onClick={() => inputRef.current?.click()} sx={{ minHeight: 44 }}>
            Upload CSV
          </Button>
        </Box>

        {parsed && (
          <>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Chip label={`${parsed.candidates.length} addresses found`} />
              {parsed.duplicates > 0 && <Chip label={`${parsed.duplicates} duplicates removed`} />}
              {parsed.invalid.length > 0 && (
                <Chip color="warning" label={`${parsed.invalid.length} invalid entries skipped`} />
              )}
            </Stack>

            {parsed.statusColumn && statusCounts.length > 0 && (
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  Block addresses with these “{parsed.statusColumn}” results
                </FormLabel>
                <FormGroup>
                  {statusCounts.map(([status, count]) => (
                    <FormControlLabel
                      key={status || BLANK_STATUS_LABEL}
                      sx={{ minHeight: 44 }}
                      control={
                        <Checkbox
                          checked={selectedStatuses.has(status)}
                          onChange={(event) => toggleStatus(status, event.target.checked)}
                        />
                      }
                      label={`${status || BLANK_STATUS_LABEL} (${count})`}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            )}

            <TextField
              label="Note (optional)"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              helperText="Saved with each address, e.g. where the list came from."
            />

            <Box>
              <Button
                variant="contained"
                disabled={busy || toAdd.length === 0}
                onClick={() => void handleSubmit()}
                startIcon={busy ? <CircularProgress size={18} color="inherit" /> : undefined}
                sx={{ minHeight: 44 }}
              >
                {busy
                  ? 'Adding…'
                  : `Add ${toAdd.length} address${toAdd.length === 1 ? '' : 'es'} to suppression list`}
              </Button>
            </Box>
          </>
        )}
      </Stack>
    </Paper>
  );
}
